import { getFamilies } from '../../api/revit/revitBridge';
import { getFamilySnapshotGrpc } from '../../api/grpc/familySnapshotClient';
import { isGrpcRuntimeAvailable } from '../../api/grpc/transport';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import {
  buildRequestedParameterIntentSignature,
  filterRequestedParameterIntents,
  normalizeRequestedParameterIntents,
  type RequestedParameterIntentInput,
} from '../../api/grpc/requestedParameterIntent';
import type { RevitFamily } from '../../api/revit/types';
import { sanitizeRevitParameterNames } from '../revitParameterNames';
import { registerRevitModelCacheInvalidator } from './modelCacheInvalidation';
import {
  cloneFamilySnapshot,
  cloneFamilySnapshotForScope,
  findSmallestReusableEntry,
  mergeScopedFamilySnapshot,
  type FamilySnapshotScope,
} from './familySnapshotData';

export { mergeScopedFamilySnapshot } from './familySnapshotData';

const DEFAULT_FAMILY_SNAPSHOT_SCOPE = '__active_revit_document__';

interface FamilySnapshotCacheEntry {
  scope: FamilySnapshotScope;
  families: RevitFamily[];
}

interface FamilySnapshotRequestEntry {
  scope: FamilySnapshotScope;
  request: Promise<FamilySnapshotResult>;
  tracksProgress: boolean;
  revision: string;
}

const familySnapshotCache = new Map<string, FamilySnapshotCacheEntry>();
const familySnapshotRequests = new Map<string, FamilySnapshotRequestEntry>();
const familySnapshotScopeRevisions = new Map<string, number>();
let familySnapshotGlobalRevision = 0;

export interface FamilySnapshotResult {
  families: RevitFamily[];
  available: boolean;
  error?: string;
}

export interface FamilySnapshotFetchOptions {
  cacheScopeKey?: string | null;
  parameterNames?: string[];
  scopedFamilyIds?: string[];
  resolveScopedFamilyIds?: (families: RevitFamily[]) => string[];
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  requestedParameters?: readonly RequestedParameterIntentInput[];
}

type FamilySnapshotReadMode = 'cache-first' | 'refresh';

function getScopeKey(cacheScopeKey?: string | null): string {
  return (cacheScopeKey?.trim() || DEFAULT_FAMILY_SNAPSHOT_SCOPE).toLowerCase();
}

function getFamilySnapshotRevision(cacheScopeKey: string): string {
  return `${familySnapshotGlobalRevision}:${familySnapshotScopeRevisions.get(cacheScopeKey) ?? 0}`;
}

function normalizeFamilySnapshotParameterNames(parameterNames?: string[]): string[] {
  return sanitizeRevitParameterNames(parameterNames ?? []);
}

function normalizeScopedFamilyIds(familyIds?: string[]): string[] {
  return Array.from(
    new Set(
      (familyIds ?? [])
        .map((familyId) => familyId.trim())
        .filter(Boolean)
    )
  );
}

function buildFamilySnapshotScope(options: {
  cacheScopeKey?: string | null;
  parameterNames?: string[];
  scopedFamilyIds?: string[];
  requestedParameters?: readonly RequestedParameterIntentInput[];
}): FamilySnapshotScope {
  return {
    cacheScopeKey: getScopeKey(options.cacheScopeKey),
    parameterKeys: Array.from(
      new Set(
        normalizeFamilySnapshotParameterNames(options.parameterNames).map((parameterName) =>
          parameterName.trim().toLowerCase()
        )
      )
    ).sort((left, right) => left.localeCompare(right)),
    familyScopeKeys: Array.from(
      new Set(
        normalizeScopedFamilyIds(options.scopedFamilyIds).map((familyId) =>
          familyId.toLowerCase()
        )
      )
    ).sort((left, right) => left.localeCompare(right)),
    intentKey: buildRequestedParameterIntentSignature(options.requestedParameters),
  };
}

function getFamilySnapshotScopeKey(scope: FamilySnapshotScope): string {
  return `${scope.cacheScopeKey}::${scope.parameterKeys.join('|')}::families=${scope.familyScopeKeys.join('|')}::intent=${scope.intentKey}`;
}

export function invalidateFamilySnapshotCache(cacheScopeKey?: string | null): void {
  if (typeof cacheScopeKey === 'undefined') {
    familySnapshotGlobalRevision += 1;
    familySnapshotCache.clear();
    return;
  }

  const scopeKey = getScopeKey(cacheScopeKey);
  familySnapshotScopeRevisions.set(
    scopeKey,
    (familySnapshotScopeRevisions.get(scopeKey) ?? 0) + 1
  );
  const scopePrefix = `${scopeKey}::`;
  Array.from(familySnapshotCache.keys()).forEach((key) => {
    if (key.startsWith(scopePrefix)) {
      familySnapshotCache.delete(key);
    }
  });
}

export function readFamilySnapshotCache(options: {
  cacheScopeKey?: string | null;
  parameterNames?: string[];
  scopedFamilyIds?: string[];
  requestedParameters?: readonly RequestedParameterIntentInput[];
}): RevitFamily[] | null {
  const requestedScope = buildFamilySnapshotScope(options);
  const cached = findSmallestReusableEntry(familySnapshotCache.values(), requestedScope);
  return cached
    ? cloneFamilySnapshotForScope(cached.families, cached.scope, requestedScope)
    : null;
}

export function rememberFamilySnapshot(options: {
  cacheScopeKey?: string | null;
  parameterNames?: string[];
  scopedFamilyIds?: string[];
  requestedParameters?: readonly RequestedParameterIntentInput[];
  families: RevitFamily[];
}): void {
  const scope = buildFamilySnapshotScope(options);
  familySnapshotCache.set(getFamilySnapshotScopeKey(scope), {
    scope,
    families: cloneFamilySnapshot(options.families),
  });
}

export async function fetchFamilySnapshot(options: {
  cacheScopeKey?: string | null;
  parameterNames?: string[];
  scopedFamilyIds?: string[];
  resolveScopedFamilyIds?: (families: RevitFamily[]) => string[];
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  requestedParameters?: readonly RequestedParameterIntentInput[];
}): Promise<RevitFamily[]> {
  const result = await fetchFamilySnapshotResult(options);
  return result.families;
}

export async function refreshFamilySnapshot(
  options: FamilySnapshotFetchOptions
): Promise<RevitFamily[]> {
  const result = await refreshFamilySnapshotResult(options);
  return result.families;
}

export async function fetchFamilySnapshotResult(
  options: FamilySnapshotFetchOptions
): Promise<FamilySnapshotResult> {
  return readFamilySnapshotResult(options, 'cache-first');
}

export async function refreshFamilySnapshotResult(
  options: FamilySnapshotFetchOptions
): Promise<FamilySnapshotResult> {
  return readFamilySnapshotResult(options, 'refresh');
}

async function readFamilySnapshotResult(
  options: FamilySnapshotFetchOptions,
  readMode: FamilySnapshotReadMode
): Promise<FamilySnapshotResult> {
  if (options.resolveScopedFamilyIds) {
    const broadResult = await readFamilySnapshotResult(
      {
        ...options,
        scopedFamilyIds: undefined,
        resolveScopedFamilyIds: undefined,
      },
      readMode
    );
    if (!broadResult.available) {
      return broadResult;
    }

    const scopedFamilyIds = normalizeScopedFamilyIds(
      options.resolveScopedFamilyIds(broadResult.families)
    );
    if (scopedFamilyIds.length === 0) {
      return broadResult;
    }

    const scopedResult = await readFamilySnapshotResult(
      {
        ...options,
        scopedFamilyIds,
        resolveScopedFamilyIds: undefined,
      },
      readMode
    );
    if (!scopedResult.available) {
      return scopedResult;
    }

    const scopedFamiliesById = new Map(
      scopedResult.families.map((family) => [family.id.trim().toLowerCase(), family])
    );
    const mergedFamilies = broadResult.families.map(
      (family) => {
        const scopedFamily = scopedFamiliesById.get(family.id.trim().toLowerCase());
        return scopedFamily
          ? mergeScopedFamilySnapshot(
              family,
              scopedFamily,
              options.parameterNames ?? []
            )
          : family;
      }
    );
    const broadFamilyIds = new Set(
      broadResult.families.map((family) => family.id.trim().toLowerCase())
    );
    scopedResult.families.forEach((family) => {
      if (!broadFamilyIds.has(family.id.trim().toLowerCase())) {
        mergedFamilies.push(family);
      }
    });

    return {
      families: cloneFamilySnapshot(mergedFamilies),
      available: true,
    };
  }

  const parameterNames = normalizeFamilySnapshotParameterNames(options.parameterNames);
  if (parameterNames.length === 0) {
    return {
      families: [],
      available: true,
    };
  }

  const requestedScope = buildFamilySnapshotScope({
    cacheScopeKey: options.cacheScopeKey,
    parameterNames,
    scopedFamilyIds: options.scopedFamilyIds,
    requestedParameters: options.requestedParameters,
  });
  const cacheKey = getFamilySnapshotScopeKey(requestedScope);

  if (readMode === 'cache-first') {
    const cached = readFamilySnapshotCache({
      cacheScopeKey: options.cacheScopeKey,
      parameterNames,
      scopedFamilyIds: options.scopedFamilyIds,
      requestedParameters: options.requestedParameters,
    });
    if (cached) {
      return {
        families: cached,
        available: true,
      };
    }
  }

  const requestRevision = getFamilySnapshotRevision(requestedScope.cacheScopeKey);
  const pendingEntry = findSmallestReusableEntry(
    familySnapshotRequests.values(),
    requestedScope,
    (entry) =>
      entry.revision === requestRevision &&
      (typeof options.onProgress !== 'function' || entry.tracksProgress)
  );
  if (pendingEntry) {
    const pendingResult = await pendingEntry.request;
    return {
      ...pendingResult,
      families: cloneFamilySnapshotForScope(
        pendingResult.families,
        pendingEntry.scope,
        requestedScope
      ),
    };
  }

  const request = requestFamilySnapshot(options, parameterNames);
  const requestEntry = {
    scope: requestedScope,
    request,
    tracksProgress: typeof options.onProgress === 'function',
    revision: requestRevision,
  };
  familySnapshotRequests.set(cacheKey, requestEntry);

  try {
    const result = await request;
    if (
      result.available &&
      requestRevision === getFamilySnapshotRevision(requestedScope.cacheScopeKey)
    ) {
      rememberFamilySnapshot({
        cacheScopeKey: options.cacheScopeKey,
        parameterNames,
        scopedFamilyIds: options.scopedFamilyIds,
        requestedParameters: options.requestedParameters,
        families: result.families,
      });
    }
    return {
      ...result,
      families: cloneFamilySnapshot(result.families),
    };
  } finally {
    if (familySnapshotRequests.get(cacheKey) === requestEntry) {
      familySnapshotRequests.delete(cacheKey);
    }
  }
}

async function requestFamilySnapshot(
  options: FamilySnapshotFetchOptions,
  parameterNames: string[]
): Promise<FamilySnapshotResult> {
  try {
    const requestedParameters = filterRequestedParameterIntents(
      normalizeRequestedParameterIntents(options.requestedParameters),
      parameterNames
    );
    if (requestedParameters.length > 0 && isGrpcRuntimeAvailable()) {
      const grpcResult = await getFamilySnapshotGrpc(parameterNames, {
        onProgress: options.onProgress,
        scopedFamilyIds: normalizeScopedFamilyIds(options.scopedFamilyIds),
        requestedParameters,
      });
      return grpcResult.success
        ? { families: grpcResult.families, available: true }
        : { families: [], available: false, error: grpcResult.error };
    }
    const result = await getFamilies(parameterNames, {
      onProgress: options.onProgress,
      scopedFamilyIds: normalizeScopedFamilyIds(options.scopedFamilyIds),
    });
    if (!result.success || !Array.isArray(result.data)) {
      return {
        families: [],
        available: false,
        error: result.error || 'Family snapshot request failed',
      };
    }

    const families = result.data;
    return {
      families,
      available: true,
    };
  } catch (error) {
    return {
      families: [],
      available: false,
      error: error instanceof Error ? error.message : 'Family snapshot request failed',
    };
  }
}

registerRevitModelCacheInvalidator(() => {
  invalidateFamilySnapshotCache();
});
